<template>
  <div class="address-form" :class="[`address-form__${theme}`, `address-form__${labelClass}` ]">
    <MicInput
      v-model="streetAddress1"
      v-validate="`required|max:${maxLength}`"
      name="Street address"
      label="Street address 1"
      placeholder="e.g. 21 Mincing Lane"
      class="address-form__field address-form__field--street"
      :error="errors.first('Street address')"
      :is-valid="isStreetAddress1Valid()"
      :required="true"
      :error-icon="errorIcon"
      :success-icon="successIcon"
    />
    <div class="address-form__row">
      <MicInput
        v-model="streetAddress2"
        v-validate="`max:${maxLength}`"
        name="streetAddress2"
        label="Street address 2 (optional)"
        placeholder="e.g. 21 Mincing Lane"
        class="address-form__field address-form__field--optional-street"
        :error="errors.first('streetAddress2')"
      />
      <MicInput
        v-model="town"
        v-validate="`required|max:${maxLength}|alpha_spaces`"
        name="Town"
        label="Town"
        placeholder="e.g. London"
        class="address-form__field address-form__field--town"
        :error="errors.first('Town')"
        :is-valid="isTownValid()"
        :required="true"
        :error-icon="errorIcon"
        :success-icon="successIcon"
      />
    </div>
    <div class="address-form__row">
      <MicInput
        v-model="county"
        v-validate="`max:${maxLength}|alpha_spaces`"
        name="county"
        label="County (optional)"
        placeholder="e.g. Greater London"
        class="address-form__field address-form__field--county"
        :error="errors.first('county')"
      />
      <MicInput
        v-model="postcodeModel"
        v-validate="'required|max:8|ukPostcode'"
        name="Postcode"
        label="Post code"
        placeholder="e.g. EC3R 7AG"
        class="address-form__field address-form__field--postcode"
        :error="errors.first('Postcode')"
        :is-valid="isPostcodeValid()"
        :required="true"
        :error-icon="errorIcon"
        :success-icon="successIcon"
      />
    </div>
    <div class="next-btn-wrap">
      <MicButton
        class="address-form__button"
        :has-arrow="!buttonIcon"
        :disabled="!isValidForm"
        :shadow="btnShadow"
        :theme="theme"
        @click="next"
      >
        Next
        <img
          v-if="buttonIcon"
          :src="require(`../assets/icons/${buttonIcon}`)"
          alt="button icon"
          class="address-form__button-icon"
        >
      </MicButton>
    </div>
  </div>
</template>

<script>
import MicInput from './MicInput'
import MicButton from '../buttons/MicButton'
import InputRegexes from '../mixins/InputRegexes'

export default {
  components: {
    MicInput,
    MicButton
  },

  mixins: [InputRegexes],

  props: {
    value: {
      type: String,
      required: true
    },
    errorIcon: {
      type: String,
      default: 'error-circle.svg'
    },
    successIcon: {
      type: String,
      default: 'check-circle-green.svg'
    },
    buttonIcon: {
      type: String,
      default: null
    },
    theme: {
      type: String,
      default: ''
    },
    labelClass: {
      type: String,
      default: ''
    },
    btnShadow: {
      type: Boolean,
      default: true
    }
  },

  data () {
    const maxLength = 32

    return {
      maxLength,
      streetAddress1: '',
      streetAddress2: '',
      town: '',
      county: '',
      dict: {
        custom: {
          'Street address': {
            required: 'Please enter your address',
            max: `Street address must be no more than ${maxLength} characters`
          },
          streetAddress2: {
            max: `Street address 2 must be no more than ${maxLength} characters`
          },
          Town: {
            required: 'Please enter your town',
            max: `Town must be no more than ${maxLength} characters`,
            'alpha_spaces': 'Town must only contain alphabetic characters as well as spaces'
          },
          county: {
            max: `County must be no more than ${maxLength} characters`,
            'alpha_spaces': 'County must only contain alphabetic characters as well as spaces'
          },
          Postcode: {
            required: 'Please enter a valid postcode',
            max: 'Please enter a valid postcode',
            ukPostcode: 'Please enter a valid postcode'
          }
        }
      }
    }
  },

  computed: {
    isValidForm () {
      return this.streetAddress1.length && this.town.length && this.postcodeModel.length && this.errors.items.length === 0
    },

    postcodeModel: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    }
  },

  created () {
    this.$validator.extend('ukPostcode', {
      validate: (value, field) => {
        return this.isPostcodeValid()
      }
    })

    this.$validator.localize('en', this.dict)
  },

  methods: {
    isStreetAddress1Valid () {
      return Boolean(this.streetAddress1) && this.streetAddress1.length <= this.maxLength
    },

    isTownValid () {
      return Boolean(this.town) && this.town.length <= this.maxLength
    },

    isPostcodeValid (value) {
      return this.postcodeRegex.test(this.postcodeModel)
    },

    next () {
      const addressText = `${this.streetAddress1},${this.streetAddress2 ? ` ${this.streetAddress2},` : ''} ${this.town},${this.county ? ` ${this.county},` : ''} ${this.postcodeModel}`
      this.$store.commit('LOAD_ADDRESS_SUCCESS', {
        Id: 'manual',
        Street: this.streetAddress1,
        SecondaryStreet: this.streetAddress2,
        City: this.town,
        District: this.county,
        PostalCode: this.postcodeModel
      })
      this.$emit('submit', { addressText, addressId: 'manual', addressPostcode: this.postcodeModel })
    }
  }
}
</script>

<style lang="scss">
.address-form {
  &__purple {
    .address-form__field .mic-input__input-container,
    .address-form__field input {
      background-color: white;
      box-shadow: unset;
      border-color: $purple-3;
    }
  }
  &__label-sm-grey {
    .mic-input__label {
      font-size: 16px;
      color: #3D404D;
      font-weight: normal;
    }
  }
}
</style>

<style lang="scss" scoped>
.address-form {
  &__row {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    .address-form__field {
      width: calc(50% - #{$spacing-12});
      margin-top: $spacing-24;

      @media(max-width: $screen-md) {
        width: 100%;
      }
    }
  }
  &__button {
    display: flex;
    align-items: center;
    justify-content: center;

    @media(max-width: $screen-sm) {
      width: 100%;
    }
  }

  &__button-icon {
    margin-left: $spacing-8;
  }
}

.next-btn-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: $spacing-24;
}

</style>
